import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Atom, Cpu, ArrowLeftRight, ShieldCheck, TrendingUp, AlertTriangle } from "lucide-react";

/**
 * Iter 145bd — Side-by-side engine comparison panel.
 * Drop into MatchDetailsPage (or any page showing a single fixture).
 *
 * Props:
 *   fixtureId: number
 */
export default function EngineComparisonPanel({ fixtureId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!fixtureId) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    axios.get(`${API}/match/${fixtureId}/compare-engines`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) setErr(e?.response?.data?.detail || "Comparison failed"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fixtureId]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-border bg-card/40 p-4 text-sm text-muted-foreground" data-testid="engine-compare-loading">
        Loading engine comparison…
      </div>
    );
  }
  if (err) {
    return (
      <div className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-4 text-sm text-rose-300" data-testid="engine-compare-error">
        {err}
      </div>
    );
  }
  if (!data) return null;

  const cfg = data.engine_config || {};
  const enginesEnabled = cfg.engines || {};
  const rec = data.recommendation || {};

  return (
    <div className="rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-500/5 via-transparent to-cyan-500/5 p-5 space-y-5" data-testid="engine-compare-panel">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <ArrowLeftRight className="w-4 h-4 text-amber-400" />
          <h3 className="font-display font-bold text-lg tracking-tight">Classic vs Quantum</h3>
        </div>
        <div className="ml-auto flex items-center gap-3 text-[11px]">
          <span className="flex items-center gap-1">
            <span className={`w-2 h-2 rounded-full ${enginesEnabled.classic ? "bg-emerald-400" : "bg-muted"}`} />
            Classic
          </span>
          <span className="flex items-center gap-1">
            <Atom className="w-3 h-3 text-cyan-400" />
            <span className={`w-2 h-2 rounded-full ${enginesEnabled.quantum ? "bg-cyan-400" : "bg-muted"}`} />
            Quantum
          </span>
        </div>
      </div>

      {/* Aggregate scores */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
        <Stat label="Agreement" value={`${Math.round((data.agreement_score || 0) * 100)}%`} accent="emerald" />
        <Stat label="Divergence" value={`${Math.round((data.divergence_score || 0) * 100)}%`} accent="amber" />
        <Stat label="Markets" value={data.n_markets} accent="cyan" />
        <Stat label="Quantum λ" value={data.quantum_meta ? `${data.quantum_meta.lam_home?.toFixed(2)}-${data.quantum_meta.lam_away?.toFixed(2)}` : "—"} accent="violet" />
      </div>

      {/* Top recommendation tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <RecTile icon={ShieldCheck} label="Safe" pick={rec.safe} testid="rec-safe" />
        <RecTile icon={TrendingUp} label="Value" pick={rec.value} testid="rec-value" />
        <RecTile icon={AlertTriangle} label="High Risk" pick={rec.high_risk} testid="rec-high-risk" />
      </div>

      {/* Pairs table */}
      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-sm" data-testid="engine-compare-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              <th className="text-left px-2 py-2">Market</th>
              <th className="text-left px-2 py-2"><Cpu className="inline w-3 h-3 mr-1" />Classic</th>
              <th className="text-left px-2 py-2"><Atom className="inline w-3 h-3 mr-1 text-cyan-400" />Quantum</th>
              <th className="text-right px-2 py-2">Δ Conf</th>
              <th className="text-center px-2 py-2">Headline</th>
            </tr>
          </thead>
          <tbody>
            {(data.pairs || []).map((p, i) => (
              <tr key={i} className="border-t border-border/30">
                <td className="px-2 py-2 font-medium text-foreground/90">{p.market_type}</td>
                <td className="px-2 py-2">
                  <PredCell m={p.classic} />
                </td>
                <td className="px-2 py-2">
                  <PredCell m={p.quantum} />
                </td>
                <td className="px-2 py-2 text-right tabular-nums">
                  {p.conf_delta != null ? (
                    <span className={p.conf_delta > 0 ? "text-cyan-300" : p.conf_delta < 0 ? "text-amber-300" : "text-muted-foreground"}>
                      {p.conf_delta > 0 ? "+" : ""}{p.conf_delta.toFixed(1)}
                    </span>
                  ) : "—"}
                </td>
                <td className="px-2 py-2 text-center">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                    p.headline_engine === "quantum"
                      ? "bg-cyan-500/10 text-cyan-300 border border-cyan-500/30"
                      : "bg-foreground/10 text-foreground/80 border border-border"
                  }`}>
                    {p.headline_engine}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Quantum meta */}
      {data.quantum_meta && (
        <details className="text-xs text-muted-foreground" data-testid="engine-compare-meta">
          <summary className="cursor-pointer font-semibold text-foreground/70">Quantum engine details</summary>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <span>E[goals]: <span className="text-foreground">{data.quantum_meta.expected_total_goals}</span></span>
            <span>Std: <span className="text-foreground">{data.quantum_meta.mc_std_total_goals}</span></span>
            <span>Sims: <span className="text-foreground">{data.quantum_meta.n_sims?.toLocaleString()}</span></span>
            <span>Version: <span className="text-foreground">{data.quantum_meta.engine_version}</span></span>
          </div>
          {data.quantum_meta.top_scores && (
            <div className="mt-2">
              Top scores:{" "}
              {data.quantum_meta.top_scores.slice(0, 5).map(([h, a, p], i) => (
                <span key={i} className="inline-block px-2 py-0.5 mr-1 bg-card/50 border border-border/40 rounded text-[10px]">
                  {h}-{a} ({(p * 100).toFixed(1)}%)
                </span>
              ))}
            </div>
          )}
        </details>
      )}
    </div>
  );
}

const Stat = ({ label, value, accent = "amber" }) => (
  <div className="rounded-xl border border-border bg-card/50 px-3 py-2.5">
    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-0.5">{label}</div>
    <div className={`text-lg font-bold tabular-nums text-${accent}-300`}>{value}</div>
  </div>
);

const RecTile = ({ icon: Icon, label, pick, testid }) => (
  <div className="rounded-xl border border-border bg-card/50 p-3" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">
      <Icon className="w-3 h-3" />
      {label}
    </div>
    {pick ? (
      <>
        <div className="text-xs text-muted-foreground">{pick.market_type}</div>
        <div className="font-semibold text-sm">{pick.prediction}</div>
        <div className="flex items-center gap-2 mt-1 text-[11px]">
          <span className="text-foreground/80">{pick.confidence}%</span>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{pick.engine}</span>
        </div>
      </>
    ) : (
      <div className="text-xs text-muted-foreground italic">No qualifying pick</div>
    )}
  </div>
);

const PredCell = ({ m }) => {
  if (!m) return <span className="text-muted-foreground">—</span>;
  return (
    <div>
      <div className="font-medium text-foreground/90 text-sm">{m.prediction}</div>
      <div className="text-[11px] text-muted-foreground">
        {m.confidence}% · {m.risk_level}
      </div>
    </div>
  );
};
